import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@tabipla/ui/components/card";
import { Badge } from "@tabipla/ui/components/badge";
import { Progress } from "@tabipla/ui/components/progress";
import {
  MapPin,
  Calendar,
  Wallet,
  Camera,
  StickyNote,
  ExternalLink,
  Luggage,
  Check,
  Sparkles,
  Star,
} from "lucide-react";
import type { BookletResponseDto, BookletSpotDto, BookletPackingItemDto } from "@tabipla/shared";
import { getGoogleMapsUrl } from "@/utils/google-maps";
import { getCategoryIconComponent, getCategoryBadgeColors } from "@/domain/constants/categories";
import {
  formatDate,
  formatDateShort,
  getDayCount,
  getDaysUntilTrip,
  formatCurrency,
  sortPackingItemsByPriority,
  countHighPriorityItems,
} from "@/utils/booklet-view";

interface BookletViewProps {
  data: BookletResponseDto;
}

function SpotCard({
  spot,
  isFirst,
  isLast,
}: {
  spot: BookletSpotDto;
  isFirst: boolean;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  const CategoryIcon = getCategoryIconComponent(spot.category) || Camera;
  const categoryColor = getCategoryBadgeColors(spot.category);

  return (
    <div className="relative pl-8">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[11px] top-8 h-[calc(100%+8px)] w-0.5 bg-gradient-to-b from-primary/50 to-primary/20" />
      )}
      {/* Timeline dot */}
      <div className="absolute left-0 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
        <CategoryIcon className="h-3 w-3" />
      </div>

      <div
        className={`rounded-xl border bg-card p-4 shadow-sm transition-all hover:shadow-md ${isFirst ? "ring-2 ring-primary/20" : ""}`}
      >
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {spot.startTime && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
              {spot.startTime}
              {spot.endTime && ` - ${spot.endTime}`}
            </span>
          )}
          <Badge variant="secondary" className={categoryColor}>
            {t(`spots.categories.${spot.category}`)}
          </Badge>
        </div>

        <h4 className="mb-2 text-lg font-bold">{spot.name}</h4>

        {spot.description && (
          <p className="mb-3 text-sm text-muted-foreground">{spot.description}</p>
        )}

        <div className="flex flex-wrap gap-3 text-sm">
          {spot.location && (
            <a
              href={getGoogleMapsUrl(spot)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900"
            >
              <MapPin className="h-3.5 w-3.5" />
              <span className="max-w-[200px] truncate">{spot.location}</span>
              <ExternalLink className="h-3 w-3 opacity-60" />
            </a>
          )}
          {spot.cost !== null && spot.cost > 0 && (
            <span className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              <Wallet className="h-3.5 w-3.5" />
              {formatCurrency(spot.cost)}
            </span>
          )}
        </div>

        {spot.notes && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <StickyNote className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{spot.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function PackingSection({ items }: { items: BookletPackingItemDto[] }) {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  const sortedItems = sortPackingItemsByPriority(items);
  const highPriorityCount = countHighPriorityItems(items);
  const totalItems = items.length;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 pb-4">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Luggage className="h-6 w-6 text-amber-600" />
          {t("booklet.packingList", "持ち物リスト")}
        </CardTitle>
        <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
          <span>
            {totalItems} {t("booklet.items", "アイテム")}
          </span>
          {highPriorityCount > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <Star className="h-4 w-4 fill-current" />
              {highPriorityCount} {t("booklet.highPriority", "重要")}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <ul className="grid gap-2 sm:grid-cols-2">
          {sortedItems.map((item, index) => (
            <li key={index} className="flex items-center gap-2 text-sm">
              <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-muted-foreground/30">
                {/* Empty checkbox for print */}
              </div>
              <span className={item.priority && item.priority >= 2 ? "font-medium" : ""}>
                {item.name}
                {item.quantity > 1 && (
                  <span className="ml-1 text-muted-foreground">×{item.quantity}</span>
                )}
              </span>
              {item.priority && item.priority >= 1 && (
                <span className="shrink-0 text-amber-500">{"★".repeat(item.priority)}</span>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function BookletView({ data }: BookletViewProps) {
  const { t } = useTranslation();
  const { trip, itineraries, packingItems, totalCost } = data;
  const dayCount = getDayCount(trip.startDate, trip.endDate);
  const daysUntil = getDaysUntilTrip(trip.startDate);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      {/* Hero Cover Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-background p-6 text-center shadow-lg">
        {/* Decorative elements */}
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />

        <div className="relative">
          {/* Cover Emoji */}
          <div className="mb-4 text-6xl">{trip.coverEmoji || "✈️"}</div>

          {/* Title */}
          <h1 className="mb-2 text-3xl font-bold tracking-tight">{trip.title}</h1>

          {/* Destination */}
          <div className="mb-4 flex items-center justify-center gap-2 text-lg text-muted-foreground">
            <MapPin className="h-5 w-5" />
            <span>{trip.destination}</span>
          </div>

          {/* Date & Duration */}
          <div className="mb-4 flex flex-wrap items-center justify-center gap-4 text-sm">
            {trip.startDate && trip.endDate && (
              <>
                <div className="flex items-center gap-2 rounded-full bg-background/80 px-4 py-2 shadow-sm backdrop-blur">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span>
                    {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                  </span>
                </div>
                {dayCount && (
                  <div className="rounded-full bg-primary px-4 py-2 font-semibold text-primary-foreground shadow-sm">
                    {dayCount}
                    {t("booklet.days")}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Countdown */}
          {daysUntil !== null && daysUntil > 0 && (
            <div className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 text-white shadow-lg">
              <Sparkles className="h-5 w-5" />
              <span className="text-lg font-bold">
                {t("booklet.countdown", "あと {{days}} 日", { days: daysUntil })}
              </span>
            </div>
          )}
          {daysUntil !== null && daysUntil === 0 && (
            <div className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-3 text-white shadow-lg">
              <Sparkles className="h-5 w-5 animate-pulse" />
              <span className="text-lg font-bold">{t("booklet.today", "いよいよ今日!")}</span>
            </div>
          )}
          {daysUntil !== null && daysUntil < 0 && (
            <div className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-3 text-white shadow-lg">
              <Check className="h-5 w-5" />
              <span className="text-lg font-bold">{t("booklet.completed", "旅行完了!")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Budget Summary */}
      {(trip.budget || totalCost > 0) && (
        <Card className="overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900">
                  <Wallet className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("booklet.estimatedCost", "予定費用")}
                  </p>
                  <p className="text-xl font-bold">{formatCurrency(totalCost)}</p>
                </div>
              </div>
              {trip.budget && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{t("booklet.budget")}</p>
                  <p className="text-lg font-semibold text-muted-foreground">
                    {formatCurrency(trip.budget)}
                  </p>
                  {trip.budget > 0 && (
                    <div className="mt-1 w-32">
                      <Progress
                        value={Math.min((totalCost / trip.budget) * 100, 100)}
                        className="h-2"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Itineraries */}
      {itineraries.map((itinerary) => (
        <Card key={itinerary.dayNumber} className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent pb-3">
            <CardTitle className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground shadow-md">
                {itinerary.dayNumber}
              </div>
              <div>
                <div className="text-lg">
                  {t("booklet.dayNumber", { day: itinerary.dayNumber })}
                </div>
                <div className="text-sm font-normal text-muted-foreground">
                  {formatDateShort(itinerary.date)}
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {itinerary.notes && (
              <div className="mb-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                <StickyNote className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{itinerary.notes}</span>
              </div>
            )}
            {itinerary.spots.length > 0 ? (
              <div className="space-y-4">
                {itinerary.spots.map((spot, spotIndex) => (
                  <SpotCard
                    key={spotIndex}
                    spot={spot}
                    isFirst={spotIndex === 0}
                    isLast={spotIndex === itinerary.spots.length - 1}
                  />
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t("booklet.noSpots")}
              </p>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Packing List */}
      <PackingSection items={packingItems} />

      {/* Footer */}
      <div className="py-6 text-center">
        <div className="mb-2 text-2xl">🧳</div>
        <p className="text-sm text-muted-foreground">
          {t("booklet.madeWith")} <span className="font-semibold">Tabitto</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground/60">
          {t("booklet.haveANiceTrip", "良い旅を!")}
        </p>
      </div>
    </div>
  );
}
